import { Empty } from "@bednbody/ui/components/empty";
import { Skeleton } from "@bednbody/ui/components/skeleton";
import { Fragment, useMemo } from "react";
import type { OpsWindowBooking } from "../command-center.hooks";

const HOURS = Array.from({ length: 16 }, (_, i) => i + 7); // 07:00–22:00 ops window
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * Day-of-week × hour booking density over the trailing 7 days. Cell opacity
 * scales against the busiest cell, so the map stays readable at pilot volume.
 */
export function BusyHoursHeatmap({
  window: rows,
  isLoading,
}: {
  window: OpsWindowBooking[];
  isLoading: boolean;
}) {
  const { grid, max, dayOrder } = useMemo(() => {
    const counts = new Map<string, number>();
    for (const b of rows) {
      if (b.status === "cancelled") continue;
      const at = new Date(b.scheduled_at);
      const key = `${at.getDay()}-${at.getHours()}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    // Trailing week: order rows oldest→today so "today" is the bottom row.
    const todayDow = new Date().getDay();
    const order = Array.from({ length: 7 }, (_, i) => (todayDow + 1 + i) % 7);
    let maxCount = 0;
    for (const v of counts.values()) maxCount = Math.max(maxCount, v);
    return { grid: counts, max: maxCount, dayOrder: order };
  }, [rows]);

  if (isLoading) {
    return <Skeleton className="h-44 motion-reduce:animate-none" />;
  }

  if (max === 0) {
    return <Empty>Not enough bookings this week to map busy hours.</Empty>;
  }

  return (
    <div className="space-y-2">
      <div
        className="grid gap-[3px]"
        style={{ gridTemplateColumns: `2.25rem repeat(${HOURS.length}, minmax(0,1fr))` }}
      >
        <span />
        {HOURS.map((h) => (
          <span key={h} className="text-center type-body-micro tabular-nums text-muted-foreground">
            {h % 3 === 1 ? h : ""}
          </span>
        ))}
        {dayOrder.map((dow) => (
          <Fragment key={dow}>
            <span className="pr-1 text-right type-body-micro leading-4 text-muted-foreground">
              {DAY_LABELS[dow]}
            </span>
            {HOURS.map((h) => {
              const count = grid.get(`${dow}-${h}`) ?? 0;
              return (
                <div
                  key={`${dow}-${h}`}
                  title={`${DAY_LABELS[dow]} ${h}:00 — ${count} booking${count === 1 ? "" : "s"}`}
                  className="aspect-square rounded-[3px] bg-primary"
                  style={{ opacity: count === 0 ? 0.06 : 0.15 + (count / max) * 0.85 }}
                />
              );
            })}
          </Fragment>
        ))}
      </div>
      <p className="text-right type-body-micro text-muted-foreground">
        darker = busier · peak {max} booking{max === 1 ? "" : "s"}/hr · last 7 days
      </p>
    </div>
  );
}
